import type { ReactElement } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchModulePanel, type ModuleSlug } from '../../api/admin.js';
import { formatNumber, formatPercent, formatDurationMs, nanoToUsdCompact, relativeTime } from '../../lib/format.js';

const SLUGS: ModuleSlug[] = ['languages', 'coach', 'fonix', 'tools', 'memory'];
// Slugs con form de configuración editable (Fase 32). El link "Configurar"
// sólo aparece para estos en la página del panel.
const SLUGS_WITH_CONFIG: ReadonlySet<string> = new Set(['videos', 'coach', 'languages', 'scout']);

export function ModulesIndex(): ReactElement {
  const configurableSlugs: string[] = ['videos', 'coach', 'languages', 'scout'];
  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Modules</h1>
          <p className="page-sub">Métricas operativas y configuración editable por módulo</p>
        </div>
      </div>
      <section className="card">
        <header className="card-head">
          <h3 className="card-title">
            Métricas <small>últimos 7 días</small>
          </h3>
        </header>
        <nav className="module-nav">
          {SLUGS.map((s) => (
            <NavLink key={s} to={`/modules/${s}`} className={({ isActive }) => (isActive ? 'is-active' : '')}>
              {s}
            </NavLink>
          ))}
        </nav>
      </section>
      <section className="card">
        <header className="card-head">
          <h3 className="card-title">
            Configuración editable <small>cambios surten efecto en la próxima sesión</small>
          </h3>
        </header>
        <nav className="module-nav">
          {configurableSlugs.map((s) => (
            <NavLink
              key={s}
              to={`/modules/${s}/config`}
              className={({ isActive }) => (isActive ? 'is-active' : '')}
            >
              {s}
            </NavLink>
          ))}
        </nav>
      </section>
    </>
  );
}

export function ModulePanelPage(): ReactElement {
  const { slug } = useParams<{ slug: ModuleSlug }>();
  const isValid = slug && (SLUGS as string[]).includes(slug);
  const query = useQuery({
    queryKey: ['admin', 'modules', slug],
    queryFn: () => fetchModulePanel(slug as ModuleSlug),
    enabled: Boolean(isValid),
  });

  if (!isValid) return <div className="loading">Módulo inválido</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Module · {slug}</h1>
          <p className="page-sub">Últimos 7 días</p>
        </div>
      </div>
      <section className="card">
        <nav className="module-nav">
          {SLUGS.map((s) => (
            <NavLink key={s} to={`/modules/${s}`} className={({ isActive }) => (isActive ? 'is-active' : '')}>
              {s}
            </NavLink>
          ))}
        </nav>
        {slug && SLUGS_WITH_CONFIG.has(slug) && (
          <p className="card-sub" style={{ marginTop: 8 }}>
            <NavLink to={`/modules/${slug}/config`}>Configurar este módulo →</NavLink>
          </p>
        )}
      </section>
      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {(query.error).message}</div>
      ) : (
        <ModuleContent slug={slug} data={query.data} />
      )}
    </>
  );
}

function ModuleContent({ slug, data }: { slug: ModuleSlug; data: Record<string, unknown> }): ReactElement {
  if (slug === 'languages') return <LanguagesPanel data={data} />;
  if (slug === 'coach') return <CoachPanel data={data} />;
  if (slug === 'fonix') return <FonixPanel data={data} />;
  if (slug === 'tools') return <ToolsPanel data={data} />;
  return <MemoryPanel data={data} />;
}

function LanguagesPanel({ data }: { data: Record<string, unknown> }): ReactElement {
  const pron = data['pronunciation'] as
    | { total: number; p50: number | null; p95: number | null; buckets: Array<{ bucket: string; n: number }> }
    | undefined;
  if (!pron) return <div className="empty">Sin datos</div>;
  return (
    <section className="card">
      <header className="card-head">
        <h3 className="card-title">Pronunciation (Azure PA)</h3>
        <small>Eventos: {formatNumber(pron.total)} · p50 {pron.p50?.toFixed(1) ?? '—'} · p95 {pron.p95?.toFixed(1) ?? '—'}</small>
      </header>
      <table className="data-table">
        <thead>
          <tr>
            <th>Bucket overall_pron_score</th>
            <th>Count</th>
          </tr>
        </thead>
        <tbody>
          {pron.buckets.map((b) => (
            <tr key={b.bucket}>
              <td>{b.bucket}</td>
              <td>{formatNumber(b.n)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function CoachPanel({ data }: { data: Record<string, unknown> }): ReactElement {
  const byLevel = (data['safetyByLevel'] ?? []) as Array<{ level: number; count: number }>;
  const bySub = (data['sessionsBySubmodule'] ?? []) as Array<{ moduleId: string; count: number }>;
  return (
    <>
      <section className="card">
        <header className="card-head">
          <h3 className="card-title">Safety classifier — eventos por level</h3>
          <small>Sin contenido — solo agregados de metadata</small>
        </header>
        <table className="data-table">
          <thead>
            <tr>
              <th>Level</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            {byLevel.length === 0 ? (
              <tr><td colSpan={2} className="empty">Sin eventos</td></tr>
            ) : (
              byLevel.map((r) => (
                <tr key={r.level}>
                  <td><span className={`pill pill-level-${r.level}`}>{r.level}</span></td>
                  <td>{formatNumber(r.count)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
      <section className="card">
        <header className="card-head">
          <h3 className="card-title">Sesiones por submódulo (7d)</h3>
        </header>
        <table className="data-table">
          <thead>
            <tr><th>module_id</th><th>Sesiones</th></tr>
          </thead>
          <tbody>
            {bySub.length === 0 ? (
              <tr><td colSpan={2} className="empty">Sin sesiones</td></tr>
            ) : (
              bySub.map((r) => (
                <tr key={r.moduleId}>
                  <td><code>{r.moduleId}</code></td>
                  <td>{formatNumber(r.count)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </>
  );
}

function FonixPanel({ data }: { data: Record<string, unknown> }): ReactElement {
  const available = data['available'];
  if (!available) {
    return <div className="empty">Fonix tables no disponibles en este entorno</div>;
  }
  const callsByState = (data['callsByState'] ?? []) as Array<{ state: string; count: number }>;
  return (
    <section className="card">
      <header className="card-head">
        <h3 className="card-title">Phone calls — por state (7d)</h3>
      </header>
      <table className="data-table">
        <thead><tr><th>State</th><th>Count</th></tr></thead>
        <tbody>
          {callsByState.length === 0 ? (
            <tr><td colSpan={2} className="empty">Sin calls</td></tr>
          ) : (
            callsByState.map((r) => (
              <tr key={r.state}><td><span className="pill">{r.state}</span></td><td>{formatNumber(r.count)}</td></tr>
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}

function ToolsPanel({ data }: { data: Record<string, unknown> }): ReactElement {
  const top = (data['topTools'] ?? []) as Array<{
    toolName: string;
    runner: string;
    calls: number;
    errors: number;
    errorRate: number;
    p50Ms: number | null;
    p95Ms: number | null;
    totalCostNano: string;
  }>;
  return (
    <section className="card">
      <header className="card-head">
        <h3 className="card-title">Top tools (7d)</h3>
      </header>
      <table className="data-table">
        <thead>
          <tr>
            <th>Tool</th>
            <th>Runner</th>
            <th>Calls</th>
            <th>Errors</th>
            <th>Error rate</th>
            <th>p50</th>
            <th>p95</th>
            <th>Cost</th>
          </tr>
        </thead>
        <tbody>
          {top.length === 0 ? (
            <tr><td colSpan={8} className="empty">Sin invocaciones</td></tr>
          ) : (
            top.map((t) => (
              <tr key={`${t.toolName}-${t.runner}`}>
                <td><code>{t.toolName}</code></td>
                <td>{t.runner}</td>
                <td>{formatNumber(t.calls)}</td>
                <td>{formatNumber(t.errors)}</td>
                <td className={t.errorRate > 0.05 ? 'bad' : ''}>{formatPercent(t.errorRate)}</td>
                <td>{formatDurationMs(t.p50Ms)}</td>
                <td>{formatDurationMs(t.p95Ms)}</td>
                <td>{nanoToUsdCompact(t.totalCostNano)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}

function MemoryPanel({ data }: { data: Record<string, unknown> }): ReactElement {
  const retrievals = (data['retrievalsByModule'] ?? []) as Array<{
    requesterModule: string;
    queries: number;
    avgHitCount: number;
    p95Ms: number | null;
  }>;
  const facts = (data['factsByScope'] ?? []) as Array<{ scope: string; count: number }>;
  return (
    <>
      <section className="card">
        <header className="card-head">
          <h3 className="card-title">Retrievals por requester module (7d)</h3>
        </header>
        <table className="data-table">
          <thead>
            <tr>
              <th>Module</th>
              <th>Queries</th>
              <th>Avg hit count</th>
              <th>p95</th>
            </tr>
          </thead>
          <tbody>
            {retrievals.length === 0 ? (
              <tr><td colSpan={4} className="empty">Sin queries</td></tr>
            ) : (
              retrievals.map((r) => (
                <tr key={r.requesterModule}>
                  <td><code>{r.requesterModule}</code></td>
                  <td>{formatNumber(r.queries)}</td>
                  <td>{r.avgHitCount.toFixed(2)}</td>
                  <td>{formatDurationMs(r.p95Ms)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
      <section className="card">
        <header className="card-head">
          <h3 className="card-title">Facts por scope</h3>
        </header>
        <table className="data-table">
          <thead><tr><th>Scope</th><th>Count</th></tr></thead>
          <tbody>
            {facts.length === 0 ? (
              <tr><td colSpan={2} className="empty">Sin facts</td></tr>
            ) : (
              facts.map((f) => (
                <tr key={f.scope}>
                  <td>{f.scope}</td>
                  <td>{formatNumber(f.count)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </>
  );
}

// Silence unused import warning
void relativeTime;
